import axios, { isAxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigationType, useSearchParams } from 'react-router-dom';

// import { AdsFilter } from './components/AdsFilter/AdsFilter';
import { AdsPageHeader } from './components/AdsPageHeader';
import ProductService from '../../services/ProductService';
import BtnPageContainer from './components/BtnPageContainer';
import Card from '../../components/Card/Card';
import CardSkeleton from '../../components/Card/CardSkeleton';
import Search from '../../components/search/Search';
import Slider from './components/Slider/Slider';
import { removeCategory, removeSubCategories, storeCategory } from '../../store/categorySlice';
import { useDebounce } from '../../hooks/useDebounce';
import { useDispatch, useSelector } from 'react-redux';
import { setFilterObjectState, resetFilterObjectState, ITEMS_PER_PAGE, resetIsSubmit, setIsSubmit } from '../../store/filtersSlice';
import useScrollRestoration from '../../hooks/useScrollRestoration';

const ProductPage = () => {
  let [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [pageAds, setPageAds] = useState({ totalAds: 0, numOfPages: 0, currentPage: 1, ads: [] });
  const [currPage, setCurrPage] = useState(parseInt(searchParams.get('page')) || 1);
  const { pathname, state } = useLocation();
  const [searchText, setSearchText] = useState(searchParams.get('q') || '');
  const search = useDebounce(searchText, 500);
  const [allCategories, setAllCategories] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [_allSubCategoriesFields, setAllSubCategoriesFields] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const filterObject = useSelector((store) => store.filtersStore);

  // Detektovanje mobilnog view-a
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640); // Mobilni je ispod 640px (iznad je tablet koji treba biti isti kao desktop)
  const currentItemsPerPage = isMobile ? 10 : ITEMS_PER_PAGE;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const setFilterObject = (filters) => {
    dispatch(setFilterObjectState(filters));
  };

  const getSliders = async () => {
    let res = await ProductService.getAllSliders();
    setSliders(res.data);
  };

  useEffect(() => {
    getSliders();
    setCurrPage(parseInt(searchParams.get('page') || 1));
  }, [location]);

  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType !== 'POP') {
      setAllSubCategories([]);
      setAllSubCategoriesFields([]);
      dispatch(removeSubCategories());
      dispatch(removeCategory());
      dispatch(resetFilterObjectState({ isSubmit: true }));
      setSearchText('');
    }
  }, [navigationType]);

  const getAllSubcategoriesFields = async (id) => {
    try {
      const response = await ProductService.getSubCategoriesFields(id);
      const subCategoriesFields = response.data;
      setAllSubCategoriesFields(subCategoriesFields);
    } catch (error) {
      console.log(error);
    }
  };

  const getFilterAds = useCallback(
    async (source) => {
      const { askForPrice, priceFrom, priceUpTo, fields, search, isSubmit, ...filterObjectRest } = filterObject;

      if (!isSubmit) {
        return;
      }

      const filtersQueryObject = {
        ...filterObjectRest,
        fields: JSON.stringify(fields),
        ...(search ? { search } : {}),
        ...(priceFrom ? { priceFrom } : {}),
        ...(priceUpTo ? { priceUpTo } : {}),
        ...(askForPrice ? { askForPrice: 1 } : {}),
        limit: currentItemsPerPage, // Koristimo dinamički broj itema
      };
      const fullPath = `products?${new URLSearchParams({ ...filtersQueryObject, page: parseInt(currPage) || 1 }).toString()}`;

      try {
        setIsLoading(true);
        // Do not clear the list if we already have ads (prevents scroll jumps)
        setPageAds((prev) => ({ ...prev, ads: prev.ads.length > 0 ? prev.ads : [] }));
        const response = await ProductService.getAds(fullPath, { cancelToken: source.token });
        const newPageAds = {
          totalAds: response.data.totalCount,
          numOfPages: Math.ceil(response.data.totalCount / currentItemsPerPage),
          currentPage: currPage || 1,
          ads: response.data.data,
        };
        setPageAds(newPageAds);
        setIsLoading(false);
        dispatch(resetIsSubmit());
      } catch (error) {
        if (isAxiosError(error)) return;
        console.log(error);
        setIsLoading(false);
        dispatch(resetIsSubmit());
      }
    },
    [filterObject, currPage, pathname, currentItemsPerPage]
  );

  useEffect(() => {
    let source = axios.CancelToken.source();
    getFilterAds(source);
    return () => {
      source.cancel();
    };
  }, [getFilterAds]);

  useEffect(() => {
    return () => {
      dispatch(setIsSubmit());
    };
  }, []);

  useEffect(() => {
    if (filterObject.search === searchText) return;
    setFilterObject({ ...filterObject, isSubmit: true, search: searchText });
  }, [search, filterObject]);

  useEffect(() => {
    if (filterObject.page === currPage) return;
    setFilterObject({ ...filterObject, isSubmit: true, page: currPage });
  }, [currPage, filterObject]);

  useEffect(() => {
    if (state?.category) dispatch(storeCategory(state.category));
    if (state?.search) setSearchText(state?.search);
    const element = document.getElementById('filters');
    if (element && state?.search && navigationType !== 'POP') {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, [state?.search, navigationType]);

  // const CardSkeletonRender = () => {
  //   return [...Array(currentItemsPerPage)].map((_el, index) => <CardSkeleton key={index + '-' + currPage}></CardSkeleton>);
  // };

  const onCurrPageChange = (page) => {
    setCurrPage(page);
    let elem = document.getElementById('search');
    if (elem && navigationType !== 'POP') elem.scrollIntoView({ behavior: 'smooth' });
  };

  useScrollRestoration(!isLoading && pageAds.ads.length > 0, pathname);

  return (
    <div>
      <AdsPageHeader
        allCategories={allCategories}
        setAllCategories={setAllCategories}
        allSubCategories={allSubCategories}
        setAllSubCategories={setAllSubCategories}
        setAllSubCategoriesFields={setAllSubCategoriesFields}
        getAllSubcategoriesFields={getAllSubcategoriesFields}
        setSearchText={setSearchText}
      />
      <div id='filters' className='flex w-full flex-col items-center px-4 pb-6 lg:flex-row lg:justify-center lg:px-11'>
        <div className='w-full max-w-[88rem]'>
          <Search setSearchText={setSearchText} numberOfAds={pageAds.totalAds} className='block pb-6 sm:hidden' searchText={searchText} />

          <div className='w-full' id='search'>
            <Search setSearchText={setSearchText} numberOfAds={pageAds.totalAds} className='hidden pb-6 sm:block' searchText={searchText} />

            {/* CARDS - Desktop layout sa banerima */}
            <div className='mt-4'>
              {/* Mobilna verzija - 5 oglasa -> baner -> baner -> 5 oglasa */}
              <div className='sm:hidden'>
                <div className='grid w-full grid-cols-1 place-items-center gap-[1rem]'>
                  {isLoading
                    ? [...Array(5)].map((_el, index) => <CardSkeleton key={index + '-' + currPage}></CardSkeleton>)
                    : pageAds.ads.slice(0, 5).map((ad) => {
                        return <Card ad={ad} key={ad._id + '-' + currPage}></Card>;
                      })}
                </div>

                {/* Baneri na mobilnom nakon 5 oglasa */}
                {!isLoading && pageAds.ads.length > 5 && (
                  <div className='my-4 flex flex-col gap-4'>
                    <Slider
                      className='h-[150px] w-full rounded-xl bg-transparent'
                      banners={sliders['slider1']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                      link={sliders['slider1']?.map((slider) => slider.link)}
                    />
                    <Slider
                      className='h-[150px] w-full rounded-xl bg-transparent'
                      banners={sliders['slider2']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                      link={sliders['slider2']?.map((slider) => slider.link)}
                    />
                  </div>
                )}

                <div className='mt-4 grid w-full grid-cols-1 place-items-center gap-[1rem]'>
                  {isLoading
                    ? [...Array(currentItemsPerPage - 5)].map((_el, index) => (
                        <CardSkeleton key={index + 5 + '-' + currPage}></CardSkeleton>
                      ))
                    : pageAds.ads.slice(5, 10).map((ad) => {
                        return <Card ad={ad} key={ad._id + '-' + currPage}></Card>;
                      })}
                </div>
              </div>

              {/* Tablet i Desktop verzija */}
              <div className='hidden sm:block'>
                {/* Prvih 15 oglasa (3 reda x 5 kolona ili 5 reda x 3 kolone za tablet) */}
                <div className='grid grid-cols-3 place-items-center gap-[1rem] xl:grid-cols-5'>
                  {isLoading
                    ? [...Array(15)].map((_el, index) => <CardSkeleton key={index + '-' + currPage}></CardSkeleton>)
                    : pageAds.ads.slice(0, 15).map((ad) => {
                        return <Card ad={ad} key={ad._id + '-' + currPage}></Card>;
                      })}
                </div>

                {/* Prvi red banera - 2 banera horizontalno */}
                {pageAds.ads.length > 15 && (
                  <div className='my-8 grid w-full grid-cols-2 gap-6'>
                    <Slider
                      className='h-[200px] w-full rounded-xl bg-transparent'
                      banners={sliders['slider1']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                      link={sliders['slider1']?.map((slider) => slider.link)}
                    />
                    <Slider
                      className='h-[200px] w-full rounded-xl bg-transparent'
                      banners={sliders['slider2']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                      link={sliders['slider2']?.map((slider) => slider.link)}
                    />
                  </div>
                )}

                {/* Sledećih 15 oglasa */}
                {pageAds.ads.length > 15 && (
                  <div className='grid grid-cols-3 place-items-center gap-[1rem] xl:grid-cols-5'>
                    {isLoading
                      ? [...Array(15)].map((_el, index) => <CardSkeleton key={index + 15 + '-' + currPage}></CardSkeleton>)
                      : pageAds.ads.slice(15, 30).map((ad) => {
                          return <Card ad={ad} key={ad._id + '-' + currPage}></Card>;
                        })}
                  </div>
                )}
              </div>
            </div>

            {/* Pagination */}
            <div className='flex w-full justify-center py-4 pt-12 md:pl-6'>
              {pageAds.numOfPages > 1 && <BtnPageContainer pageAds={pageAds} setPageAds={setPageAds} setCurrPage={onCurrPageChange} />}
            </div>

            {/* Baneri ispod paginacije - samo desktop */}
            {/* <div className='mt-8 hidden w-full grid-cols-2 gap-6 sm:grid'>
              <Slider
                className='h-[200px] w-full rounded-xl bg-transparent'
                banners={sliders['slider4']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                link={sliders['slider4']?.map((slider) => slider.link)}
              />
              <Slider
                className='h-[200px] w-full rounded-xl bg-transparent'
                banners={sliders['slider5']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                link={sliders['slider5']?.map((slider) => slider.link)}
              />
            </div> */}
          </div>
        </div>

        {/* Stari banneri sa desne strane - potpuno sakriveni */}
        <div className='hidden'>{/* ... svi stari Slider elementi ... */}</div>

        {/* Mobilni banneri ispod paginacije */}
        {/* <div className='mt-[2.875rem] h-full w-full place-items-end gap-4 rounded-xl sm:hidden'>
          <div className='mx-auto flex w-full items-center justify-center gap-6'>
            <Slider
              className='w-full rounded-xl bg-transparent break16:h-[270px]'
              banners={sliders['slider4']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
              link={sliders['slider4']?.map((slider) => slider.link)}
            />
            <Slider
              className='w-full rounded-xl bg-transparent break16:h-[270px]'
              banners={sliders['slider5']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
              link={sliders['slider5']?.map((slider) => slider.link)}
            />
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default ProductPage;
